// «/»-меню: в начале абзаца или после пробела наберите «/» - список вставляемых блоков с поиском
// («/рис», «/табл», «/форм», «/раздел»). Стрелки - выбор, Enter/Tab - вставить, Esc - закрыть.
// Список берётся из реестра команд, поэтому новые вставки других функций появляются здесь сами.
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { registry } from '../../core/registry';
import { runCommand } from '../../core/ctx';
import { Icon } from '../../ui/icons';
import { prettyShortcut } from '../../ui/controls';

const ORDER = ['block.h1', 'block.h2', 'block.h3', 'block.paragraph', 'list.dash', 'list.letter', 'list.digit',
  'insert.figure', 'insert.table', 'insert.listing', 'math.formula', 'math.inline', 'insert.note', 'insert.example',
  'refs.cite', 'refs.xref', 'refs.footnote', 'insert.pageBreak', 'sto.appendix'];
const WORDS: Record<string, string> = {
  'block.h1': 'раздел заголовок глава h1', 'block.h2': 'подраздел заголовок h2', 'block.h3': 'пункт заголовок h3',
  'block.paragraph': 'текст абзац обычный', 'list.dash': 'перечисление список дефис', 'list.letter': 'перечисление список буквы',
  'list.digit': 'перечисление список цифры нумерованный', 'insert.figure': 'рисунок картинка изображение скриншот',
  'insert.table': 'таблица', 'insert.listing': 'листинг код программа', 'math.formula': 'формула уравнение',
  'math.inline': 'формула строчная в строке', 'insert.note': 'примечание', 'insert.example': 'пример',
  'refs.cite': 'источник ссылка литература', 'refs.xref': 'ссылка перекрёстная на рисунок таблицу',
  'refs.footnote': 'сноска', 'insert.pageBreak': 'разрыв страницы новая', 'sto.appendix': 'приложение',
};
const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е');

interface SlashState { open: boolean; from: number; to: number; query: string; x: number; y: number; sel: number }
let state: SlashState = { open: false, from: 0, to: 0, query: '', x: 0, y: 0, sel: 0 };
let dismissedAt = -1;
const listeners = new Set<() => void>();
const setState = (p: Partial<SlashState>) => { state = { ...state, ...p }; listeners.forEach((f) => f()); };
const store = { get: () => state, subscribe: (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; } };
let viewRef: EditorView | null = null;

interface SlashItem { id: string; title: string; desc?: string; icon?: string; shortcut?: string }

function allItems(): SlashItem[] {
  const ids = [...ORDER];
  for (const c of registry.commands.list()) {
    if ((c.id.startsWith('insert.') || c.id.startsWith('sto.struct.')) && !ids.includes(c.id)) ids.push(c.id);
  }
  return ids.map((id) => registry.commands.get(id)).filter(Boolean)
    .map((c) => ({ id: c!.id, title: c!.title, desc: c!.description, icon: c!.icon, shortcut: c!.shortcut }));
}

export function filterSlash(query: string): SlashItem[] {
  const q = norm(query);
  const items = allItems();
  if (!q) return items;
  return items.map((it) => {
    const words = norm(`${it.title} ${WORDS[it.id] || ''}`).split(/[\s«»()/,.-]+/);
    const s = words.some((w) => w.startsWith(q)) ? 2 : norm(it.title).includes(q) ? 1 : 0;
    return [it, s] as const;
  }).filter(([, s]) => s > 0).sort((a, b) => b[1] - a[1]).map(([it]) => it);
}

function update(view: EditorView) {
  viewRef = view;
  const { selection } = view.state;
  const $from = selection.$from;
  const close = () => { if (state.open) setState({ open: false }); };
  if (!selection.empty || !view.hasFocus() || $from.parent.type.name !== 'paragraph') return close();
  const before = $from.parent.textBetween(Math.max(0, $from.parentOffset - 40), $from.parentOffset, undefined, '￼');
  const m = before.match(/(?:^|\s)\/([\p{L}\p{N}]{0,24})$/u);
  if (!m) { dismissedAt = -1; return close(); }
  const from = $from.pos - m[1].length - 1;
  if (from === dismissedAt) return close();
  if (!filterSlash(m[1]).length) return close();
  const c = view.coordsAtPos(from);
  setState({ open: true, from, to: $from.pos, query: m[1], x: c.left, y: c.bottom, sel: state.open && state.from === from ? state.sel : 0 });
}

function choose(view: EditorView, it: SlashItem | undefined) {
  if (!it) return;
  const { from, to } = state;
  setState({ open: false });
  view.dispatch(view.state.tr.delete(from, to));
  view.focus();
  setTimeout(() => runCommand(it.id), 0);
}

export const SlashExtension = Extension.create({
  name: 'stoSlash',
  addProseMirrorPlugins() {
    return [new Plugin({
      key: new PluginKey('stoSlash'),
      view: (v) => {
        viewRef = v;
        return { update: (view) => update(view), destroy: () => { setState({ open: false }); viewRef = null; } };
      },
      props: {
        handleKeyDown: (view, e) => {
          if (!state.open) return false;
          const items = filterSlash(state.query);
          if (e.key === 'ArrowDown') { setState({ sel: (state.sel + 1) % items.length }); return true; }
          if (e.key === 'ArrowUp') { setState({ sel: (state.sel - 1 + items.length) % items.length }); return true; }
          if (e.key === 'Enter' || e.key === 'Tab') { choose(view, items[state.sel]); return true; }
          if (e.key === 'Escape') { dismissedAt = state.from; setState({ open: false }); return true; }
          return false;
        },
        handleDOMEvents: { blur: () => { setTimeout(() => { if (viewRef && !viewRef.hasFocus()) setState({ open: false }); }, 150); return false; } },
      },
    })];
  },
});

export function SlashMenu() {
  const s = useSyncExternalStore(store.subscribe, store.get);
  const items = useMemo(() => (s.open ? filterSlash(s.query) : []), [s.open, s.query]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { (ref.current?.querySelector('.slash-item.is-sel') as HTMLElement | null)?.scrollIntoView({ block: 'nearest' }); }, [s.sel]);
  if (!s.open || !items.length) return null;
  const h = Math.min(360, items.length * 44 + 34);
  const top = s.y + 6 + h > window.innerHeight ? s.y - h - 26 : s.y + 6;
  return createPortal(
    <div className="slash-menu" ref={ref} style={{ left: Math.min(s.x, window.innerWidth - 330), top }} role="listbox" data-testid="slash-menu"
      onMouseDown={(e) => e.preventDefault()}>
      <div className="slash-head">{s.query ? `Вставить: «${s.query}»` : 'Вставить блок'}</div>
      {items.map((it, i) => (
        <div key={it.id} role="option" aria-selected={i === s.sel} className={'slash-item' + (i === s.sel ? ' is-sel' : '')}
          data-testid={'slash-' + it.id} onMouseMove={() => { if (s.sel !== i) setState({ sel: i }); }}
          onClick={() => viewRef && choose(viewRef, it)}>
          <span className="slash-ico"><Icon name={it.icon} size={18} /></span>
          <span className="slash-text"><span className="slash-title">{it.title}</span>{it.desc && <span className="slash-desc">{it.desc}</span>}</span>
          {it.shortcut && <span className="slash-key">{prettyShortcut(it.shortcut)}</span>}
        </div>
      ))}
    </div>, document.body);
}
